<script setup>
import { useUserStore } from "@/stores";
import useVuelidate from "@vuelidate/core";
import {
  email,
  helpers,
  minLength,
  required,
  sameAs,
} from "@vuelidate/validators";
import { computed, onMounted, ref } from "vue";

const userStore = useUserStore();

const user = userStore.user;

const form = ref({});

const loading = ref(false);

const passwordRef = computed(() => form.value.password);

const showPass = ref(false);

const showConfPass = ref(false);

async function handleSubmit() {
  loading.value = true;
  await userStore.updateUserData(form.value);
  loading.value = false;
}

function removeImage() {
  form.value.imageLink = null;
}

const rules = {
  email: {
    required: helpers.withMessage("Email is required", required),
    email: helpers.withMessage("Invalid email format", email),
  },
  password: {
    required: helpers.withMessage("Password is required", required),
    minLength: helpers.withMessage(
      "Password must be at least 8 characters",
      minLength(8)
    ),
  },
  confirmPassword: {
    required: helpers.withMessage("Confirm password is required", required),
    sameAsPassword: helpers.withMessage(
      "Passwords do not match",
      sameAs(passwordRef)
    ),
  },
};

const v$ = useVuelidate(rules, form);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

onMounted(() => {
  form.value = { ...user };
  form.value.password = null;
  form.value.confirmPassword = null;
});
</script>

<template>
  <div class="d-flex flex-column pa-10">
    <h1 class="mb-10 page-title">User Settings</h1>
    <div class="d-flex w-50">
      <v-form class="d-flex flex-column gap-10 w-100">
        <v-file-input
          v-if="form.imageLink == null"
          v-model="form.image"
          accept="image/*"
          label="Upload Photo"
          variant="outlined"
          prepend-icon=""
          prepend-inner-icon="mdi-camera"
          hide-details="auto"
        />

        <div
          v-if="form.imageLink != null"
          class="image-preview d-flex flex-column gap-10 align-center justify-space-between"
        >
          <img :src="form.imageLink" />
          <v-btn
            size="small"
            color="error"
            variant="outlined"
            @click="removeImage()"
          >
            Remove Image
          </v-btn>
        </div>
        <v-text-field
          id="id"
          v-model="form.name"
          name="name"
          label="Name"
          :error-messages="getErrorMessages('name').value"
          hide-details="auto"
          @blur="v$.name.$touch()"
        />
        <v-text-field
          id="id"
          v-model="form.email"
          name="email"
          type="email"
          variant="outlined"
          :error-messages="getErrorMessages('email').value"
          hide-details="auto"
          @blur="v$.email.$touch()"
        >
          <template #label> Email </template>
        </v-text-field>
        <v-text-field
          id="id"
          v-model="form.password"
          name="password"
          :type="showPass ? 'text' : 'password'"
          label="Password"
          variant="outlined"
          :append-inner-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
          :error-messages="getErrorMessages('password').value"
          hide-details="auto"
          @click:append-inner="showPass = !showPass"
          @blur="v$.password.$touch()"
        />

        <v-text-field
          id="id"
          v-model="form.confirmPassword"
          name="password"
          :type="showConfPass ? 'text' : 'password'"
          label="Confirm Password"
          variant="outlined"
          :append-inner-icon="showConfPass ? 'mdi-eye' : 'mdi-eye-off'"
          :error-messages="getErrorMessages('confirmPassword').value"
          hide-details="auto"
          @click:append-inner="showConfPass = !showConfPass"
          @blur="v$.confirmPassword.$touch()"
        />
        <div class="mb-5 d-flex justify-space-between align-center">
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="handleSubmit()"
          >
            Save changes
          </v-btn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-preview {
  img {
    max-width: 150px;
    border-radius: 8px;
  }
}
</style>
